import type { TFunction } from 'i18next'
import type { QuestionLearner, QuestionStat } from '~/gql/questions/questions'
import { userDisplayName } from '~/helpers/users/display-name'
import { expectedAnswer, SCORE_BRACKETS } from './question-display'

type Table = { headers: string[], rows: (string | number)[][] }

const score = (value: number | null | undefined) => value == null ? '' : Math.round(value)

/**
 * Export « CSV question » (#159), aux colonnes du rapport de l'ancienne
 * plateforme : une ligne par question, sa synthèse, puis la réponse et le
 * score de chaque apprenant — ce que ses clients ont l'habitude de relire.
 */
export function questionCsv(
  questions: QuestionStat[],
  learners: QuestionLearner[],
  context: { pathName: string, moduleTitle: string },
  t: TFunction,
): Table {
  const headers = [
    t('questions.csv.path'),
    t('questions.csv.module'),
    t('questions.csv.respondents'),
    t('questions.csv.question'),
    t('questions.csv.expected'),
    t('questions.csv.averageScore'),
    ...SCORE_BRACKETS.map(range => `${range} %`),
    ...learners.flatMap((learner) => {
      const name = userDisplayName(learner)
      return [t('questions.csv.learnerResponse', { name }), t('questions.csv.learnerScore', { name })]
    }),
  ]
  const rows = questions.map((question) => {
    const byLearner = new Map((question.answers ?? []).map(answer => [answer.userId, answer]))
    return [
      context.pathName,
      context.moduleTitle,
      question.respondents,
      question.description ?? question.interactionId,
      expectedAnswer(question.correctResponses),
      score(question.averageScore),
      ...question.buckets,
      ...learners.flatMap((learner) => {
        const answer = byLearner.get(learner.id)
        return [answer?.response ?? '', score(answer?.score)]
      }),
    ]
  })
  return { headers, rows }
}

/** Export « CSV réponses » : une ligne par apprenant et par question, pour filtrer dans un tableur. */
export function answersCsv(questions: QuestionStat[], learners: QuestionLearner[], t: TFunction): Table {
  const byId = new Map(learners.map(learner => [learner.id, learner]))
  const headers = [
    t('questions.csv.learner'),
    t('questions.csv.email'),
    t('questions.csv.questionId'),
    t('questions.csv.question'),
    t('questions.csv.response'),
    t('questions.csv.result'),
    t('questions.csv.score'),
    t('questions.csv.attempt'),
  ]
  const rows = questions.flatMap(question => (question.answers ?? []).map((answer) => {
    const learner = byId.get(answer.userId)
    return [
      learner ? userDisplayName(learner) : '',
      learner?.email ?? '',
      question.interactionId,
      question.description ?? '',
      answer.response ?? '',
      answer.result ?? '',
      score(answer.score),
      answer.attemptNumber,
    ]
  }))
  return { headers, rows }
}
